import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import { getJobStatus } from '../../utils';
import { money, quantity } from './format';

const BREAKDOWN_LABELS = {
  SUBMITTED: 'Submitted',
  UNSUBMITTED: 'Not submitted',
  NONE: 'None',
};

const costOf = figures =>
  figures ? figures.labor + figures.material + figures.fuel + figures.insurance : 0;

const sumOf = (rows, pick) => rows.reduce((total, row) => total + pick(row), 0);

//a stored run's dates are calendar dates sent as UTC midnight
const shiftDate = value =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

const JobRows = ({ rows, unitKey, muted }) =>
  rows.map(row => (
    <tr
      key={row.jobId}
      className={`border-b border-navy-light ${muted ? 'text-opaque-job-details-gray' : ''}`}
    >
      <td className='py-1.5 pr-4'>
        <div className='text-dark-blue'>
          {row.jobName || row.address}
          {/*a flash's figures count, but it adds no pool to the pools finished*/}
          {row.isFlash && (
            <span className='ml-2 text-xs font-semibold text-opaque-job-details-gray'>Flash</span>
          )}
        </div>
        <div className='text-xs text-opaque-job-details-gray'>
          {row.jobName ? `${row.address}, ${row.city}` : row.city}
        </div>
      </td>
      <td className='py-1.5 pr-4 whitespace-nowrap'>{shiftDate(row.lastShiftDate)}</td>
      <td className='py-1.5 pr-4 whitespace-nowrap'>
        {getJobStatus(Object.fromEntries(row.status.map(field => [field, true])))}
      </td>
      <td
        className={`py-1.5 pr-4 whitespace-nowrap ${row.counted ? '' : 'font-semibold text-gunite-red'}`}
      >
        {BREAKDOWN_LABELS[row.breakdownState]}
      </td>
      <td className='py-1.5 text-right'>{row.figures ? quantity(row.figures[unitKey]) : '–'}</td>
      <td className='py-1.5 text-right'>{row.figures ? money(row.figures.sales) : '–'}</td>
      <td className='py-1.5 text-right'>{row.figures ? money(costOf(row.figures)) : '–'}</td>
      <td className='py-1.5 text-right'>{row.figures ? money(row.figures.profit) : '–'}</td>
    </tr>
  ));

const TotalRow = ({ label, rows, unitKey }) => (
  <tr className='border-b border-navy-light font-semibold'>
    <td className='py-1.5 pr-4' colSpan={4}>
      {label}
    </td>
    <td className='py-1.5 text-right'>
      {quantity(sumOf(rows, row => row.figures?.[unitKey] ?? 0))}
    </td>
    <td className='py-1.5 text-right'>{money(sumOf(rows, row => row.figures?.sales ?? 0))}</td>
    <td className='py-1.5 text-right'>{money(sumOf(rows, row => costOf(row.figures)))}</td>
    <td className='py-1.5 text-right'>{money(sumOf(rows, row => row.figures?.profit ?? 0))}</td>
  </tr>
);

//The jobs behind one branch's figures. Jobs left out of the figures lead, since finding them is the
//point: most are breakdowns unlocked by the Edit button after the job was completed, costed here as
//they stand so the gap they leave can be read off.
//
//The rows are stored with the run, as the jobs stood when it was made, so they add up to its totals.
//A row's status is the names of the job's status timestamps that were set at the time.
const ReportJobs = ({ detail, onOpen, locationId, isPlaster }) => {
  const [expanded, setExpanded] = useState(false);
  const unitKey = isPlaster ? 'batches' : 'yards';
  const unitLabel = isPlaster ? 'Batches' : 'Yards';

  const toggle = () => {
    if (!expanded) onOpen();
    setExpanded(!expanded);
  };

  const rows = detail.data?.locations.find(section => section.location.id === locationId)?.jobs;
  const counted = rows?.filter(row => row.counted) ?? [];
  const notCounted = rows?.filter(row => !row.counted) ?? [];

  return (
    <div className='flex flex-col gap-3'>
      <button
        type='button'
        aria-expanded={expanded}
        onClick={toggle}
        className='flex items-center gap-2 w-fit text-sm font-semibold text-dark-blue'
      >
        <FaChevronDown className={`h-3 w-3 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        Jobs behind these figures
      </button>

      {expanded && detail.status === 'loading' && <LoadingSpinner />}

      {expanded && detail.status === 'error' && (
        <p className='text-sm text-gunite-red'>{detail.error}</p>
      )}

      {expanded && rows && (
        <>
          {!rows.length ? (
            <p className='text-sm text-opaque-job-details-gray'>No jobs fall in this period.</p>
          ) : (
            <div className='w-full overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-navy-light text-left'>
                    <th className='py-1.5 pr-4 font-semibold'>Job</th>
                    <th className='py-1.5 pr-4 font-semibold'>Last shift</th>
                    <th className='py-1.5 pr-4 font-semibold'>Job status</th>
                    <th className='py-1.5 pr-4 font-semibold'>Breakdown</th>
                    <th className='py-1.5 font-semibold text-right'>{unitLabel}</th>
                    <th className='py-1.5 font-semibold text-right'>Sales</th>
                    <th className='py-1.5 font-semibold text-right'>Cost</th>
                    <th className='py-1.5 font-semibold text-right'>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {notCounted.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={8} className='pt-3 pb-1 font-semibold text-gunite-red'>
                          Not counted ({notCounted.length})
                        </td>
                      </tr>
                      <JobRows rows={notCounted} unitKey={unitKey} muted />
                      <TotalRow
                        label='Left out of the figures'
                        rows={notCounted}
                        unitKey={unitKey}
                      />
                    </>
                  )}
                  <tr>
                    <td colSpan={8} className='pt-3 pb-1 font-semibold text-dark-blue'>
                      Counted ({counted.length})
                    </td>
                  </tr>
                  <JobRows rows={counted} unitKey={unitKey} />
                  <TotalRow label='Counted total' rows={counted} unitKey={unitKey} />
                </tbody>
              </table>
            </div>
          )}

          <p className='text-sm text-opaque-job-details-gray'>
            Jobs are shown as they stood when this report was run. A breakdown that is not submitted
            is costed here but left out of the figures. One on a Completed job was most likely
            submitted and then reopened with Edit.
          </p>
        </>
      )}
    </div>
  );
};

export default ReportJobs;
